import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Trash2, Eye, EyeOff, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = { name: "", role: "", cred: "", img: "", bio: "", linkedin: "", order: 0, published: false, note: "" };
const err = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export default function AdminMentorsPage() {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const load = () => api.get("/admin/mentors").then((r) => setRows(r.data)).catch((e) => toast.error(err(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/admin/mentors", { ...draft, order: Number(draft.order) || 0 }); toast.success("Mentor added (unpublished)"); setDraft(EMPTY); load(); }
    catch (e) { toast.error(err(e)); }
  };

  return (
    <div data-testid="admin-mentors-page" className="mx-auto max-w-6xl px-4 py-8 text-white sm:px-8">
      <p className="text-xs font-semibold uppercase tracking-wider text-cyan-400">Admin</p>
      <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold sm:text-3xl"><Users className="h-6 w-6 text-cyan-400" /> Mentor profiles</h1>
      <p className="mt-1 text-xs text-slate-500">Controls the "Meet our mentors" section on /about. Only <b>published</b> rows are public. Do not publish unverified names, photos or credentials.</p>

      <section className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <h2 className="text-sm font-bold">Add mentor</h2>
        <Fields m={draft} set={setDraft} prefix="new" />
        <button data-testid="mentor-create-btn" onClick={create} disabled={!draft.name || !draft.role} className="mt-3 flex items-center gap-1 rounded-lg bg-cyan-400 px-4 py-2 text-xs font-bold text-[#041016] disabled:opacity-50"><Plus className="h-3.5 w-3.5" /> Add (draft)</button>
      </section>

      <div className="mt-6 space-y-3">
        {rows === null ? <p className="text-xs text-slate-500">Loading…</p> : rows.map((m) => <Row key={m.id} m={m} onChanged={load} />)}
        {rows?.length === 0 && <p className="text-xs text-slate-500">No mentors yet — the About page shows neutral placeholders.</p>}
      </div>
    </div>
  );
}

const Row = ({ m, onChanged }) => {
  const [v, setV] = useState(m);
  useEffect(() => { setV((prev) => ({ ...prev, published: m.published, updated_at: m.updated_at })); }, [m.published, m.updated_at]);
  const save = async (patch = {}) => {
    const body = { ...v, published: m.published, ...patch, order: Number(v.order) || 0 };
    try { await api.put(`/admin/mentors/${m.id}`, body); toast.success(body.published !== m.published ? (body.published ? "Published" : "Unpublished") : "Saved"); onChanged(); }
    catch (e) { toast.error(err(e)); }
  };
  const del = async () => { if (!window.confirm(`Delete "${m.name}"?`)) return; try { await api.delete(`/admin/mentors/${m.id}`); toast.success("Deleted"); onChanged(); } catch (e) { toast.error(err(e)); } };
  return (
    <div data-testid="mentor-row" className={`rounded-2xl border p-4 ${m.published ? "border-emerald-400/20 bg-emerald-400/[0.03]" : "border-amber-400/20 bg-amber-400/[0.03]"}`}>
      <div className="flex flex-wrap items-center gap-3">
        {v.img ? <img src={v.img} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/5"><Users className="h-5 w-5 text-slate-600" /></div>}
        <div className="flex-1"><p className="text-sm font-bold">{m.name} <span className="text-xs font-normal text-slate-400">— {m.role}</span></p>
          <p className="text-[11px] text-slate-500">{m.published ? "PUBLISHED" : "DRAFT (not public)"}{m.note ? ` · ${m.note}` : ""}</p></div>
        <button data-testid="mentor-toggle-publish-btn" onClick={() => save({ published: !m.published })} className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold ${m.published ? "border border-white/10" : "bg-emerald-500/80"}`}>{m.published ? <><EyeOff className="h-3.5 w-3.5" /> Unpublish</> : <><Eye className="h-3.5 w-3.5" /> Publish</>}</button>
        <button data-testid="mentor-delete-btn" onClick={del} className="rounded-lg border border-rose-400/30 px-3 py-1.5 text-xs text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <Fields m={v} set={setV} prefix={m.id} />
      <button data-testid="mentor-save-btn" onClick={() => save()} className="mt-3 flex items-center gap-1 rounded-lg bg-cyan-400 px-4 py-1.5 text-xs font-bold text-[#041016]"><Save className="h-3.5 w-3.5" /> Save changes</button>
    </div>
  );
};

const Fields = ({ m, set, prefix }) => (
  <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
    {[["name", "Full name (verified)"], ["role", "Role / title"], ["cred", "Credentials (one line)"], ["img", "Photo URL (e.g. /mentor-1.jpg)"], ["linkedin", "LinkedIn URL (optional)"], ["order", "Display order"], ["note", "Internal note (never public)"]].map(([k, l]) => (
      <label key={k} className="text-slate-500">{l}
        <input data-testid={`mentor-${k}-input-${prefix}`} value={m[k] ?? ""} onChange={(e) => set({ ...m, [k]: e.target.value })} type={k === "order" ? "number" : "text"}
          className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white" /></label>))}
    <label className="text-slate-500 lg:col-span-3">Short bio (optional, public)
      <textarea data-testid={`mentor-bio-input-${prefix}`} value={m.bio ?? ""} onChange={(e) => set({ ...m, bio: e.target.value })} rows={2} className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white" /></label>
  </div>
);
